/**
 * FilmMomentPicker — opens from the community composer's "Film moment"
 * button. Coach picks one of their own films, sets a timestamp (or
 * picks an existing note's timestamp), and the picker hands back a
 * fully-formed film_clip embed for the composer to attach.
 *
 * Two-step flow:
 *   Step 1: list of the coach's films with thumbnails. Click one.
 *   Step 2: timestamp input + optional title override + Attach.
 *
 * The embed payload format mirrors buildFilmClipEmbed in filmApi.js
 * so feed render is identical to share-from-note.
 */
import React, { useEffect, useState } from "react";
import { X, Loader2, Film, ArrowLeft, Plus, Search, Clock } from "lucide-react";
import { GOLD } from "../constants/theme";
import { listFilms, listNotesForFilm, formatTime, extractYouTubeId } from "../film/filmApi";

export function FilmMomentPicker({ onAttach, onClose }) {
  const [films, setFilms] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);  // a film row
  const [notes, setNotes] = useState(null);        // notes for selected film
  const [timestamp, setTimestamp] = useState(0);
  const [titleOverride, setTitleOverride] = useState("");

  useEffect(() => {
    let cancelled = false;
    listFilms()
      .then(f => { if (!cancelled) setFilms(f); })
      .catch(e => { if (!cancelled) setError(e?.message || "Couldn't load films"); });
    return () => { cancelled = true; };
  }, []);

  // When a film is picked, also load its notes so the coach can
  // optionally jump to an existing tag instead of typing a timestamp.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    listNotesForFilm(selected.id)
      .then(n => { if (!cancelled) setNotes(n); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [selected]);

  const filtered = (films || []).filter(f =>
    !search.trim() || (f.title || "").toLowerCase().includes(search.toLowerCase())
  );

  const attach = (note) => {
    if (!selected) return;
    const ytId = selected.provider === "youtube" ? extractYouTubeId(selected.external_url) : null;
    const ts = note ? note.timestamp_seconds : timestamp;
    const fallbackTitle = note?.title || note?.body || titleOverride.trim() || selected.title || "Clip";
    onAttach?.({
      type: "film_clip",
      youtube_url: selected.external_url || null,
      youtube_id: ytId,
      thumbnail_url: note?.image_url || selected.thumbnail_url || null,
      timestamp: ts || 0,
      title: fallbackTitle,
      body: note?.body && note?.title ? note.body : "",
      snapshot_url: note?.image_url || null,
      audio_url: note?.audio_url || null,
      source_film_kind: selected.kind || "general",
      source_note_id: note?.id || null,
    });
  };

  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,zIndex:5500,background:"rgba(0,0,0,0.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(6px)"}}>
      <div onClick={e => e.stopPropagation()}
        style={{
          background:"linear-gradient(180deg, #131313, #0A0A0A)",
          border:"1px solid rgba(226,221,159,0.30)",
          borderRadius:16,padding:"22px 24px",
          maxWidth:640,width:"100%",maxHeight:"86vh",
          display:"flex",flexDirection:"column",overflow:"hidden",
          boxShadow:"0 24px 70px rgba(0,0,0,0.7)",
          fontFamily:"'Inter', sans-serif",color:"#F5F5F5",position:"relative",
        }}>
        <button onClick={onClose} aria-label="Close"
          style={{position:"absolute",top:14,right:14,background:"transparent",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",padding:6,display:"flex"}}>
          <X size={16}/>
        </button>

        <div style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.6,textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
          <Film size={11}/> Film Moment
        </div>
        <h2 style={{fontFamily:"'Bebas Neue', sans-serif",fontSize:28,letterSpacing:1.5,color:"#fff",margin:"0 0 14px",lineHeight:1}}>
          {selected ? "PICK A MOMENT" : "PICK A FILM"}
        </h2>

        {error && (
          <div style={{padding:"8px 12px",background:"rgba(255,107,107,0.10)",border:"1px solid rgba(255,107,107,0.40)",borderRadius:8,color:"#FF9999",fontSize:12,marginBottom:12}}>
            {error}
          </div>
        )}

        {!selected && (
          <>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:9,border:"1px solid rgba(255,255,255,0.10)",background:"rgba(255,255,255,0.03)",marginBottom:12}}>
              <Search size={13} color="rgba(255,255,255,0.5)"/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search your films…"
                style={{flex:1,background:"transparent",border:"none",outline:"none",color:"#F5F5F5",fontSize:13,fontFamily:"inherit"}}/>
            </div>
            <div style={{flex:1,minHeight:0,overflow:"auto",display:"flex",flexDirection:"column",gap:8,paddingRight:4}}>
              {films === null ? (
                <div style={{textAlign:"center",padding:"30px 0",color:"rgba(255,255,255,0.5)"}}>
                  <Loader2 size={18} style={{animation:"spin 1s linear infinite"}}/>
                </div>
              ) : filtered.length === 0 ? (
                <div style={{textAlign:"center",padding:"30px 0",color:"rgba(255,255,255,0.5)",fontSize:13}}>
                  {search ? "No matches." : "No films yet. Add one in Film Studio first."}
                </div>
              ) : filtered.map(f => (
                <button key={f.id} onClick={()=>{ setSelected(f); setTimestamp(0); setTitleOverride(""); }}
                  style={{
                    display:"flex",alignItems:"center",gap:12,padding:"8px 10px",borderRadius:10,
                    background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",
                    color:"#F5F5F5",fontFamily:"inherit",cursor:"pointer",textAlign:"left",
                  }}>
                  <div style={{width:80,aspectRatio:"16/9",background:"#000",borderRadius:6,overflow:"hidden",flexShrink:0}}>
                    {f.thumbnail_url && <img src={f.thumbnail_url} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.title}</div>
                    <div style={{fontSize:10.5,color:"rgba(255,255,255,0.5)",letterSpacing:.4,textTransform:"uppercase",marginTop:2}}>{f.kind} · {f.provider || "–"}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {selected && (
          <>
            <button onClick={()=>{ setSelected(null); setNotes(null); }}
              style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:7,background:"transparent",color:"rgba(255,255,255,0.6)",border:"1px solid rgba(255,255,255,0.12)",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",alignSelf:"flex-start",marginBottom:14}}>
              <ArrowLeft size={11}/> Back to films
            </button>

            {/* Selected film card */}
            <div style={{display:"flex",gap:12,padding:"10px 12px",borderRadius:10,background:`${GOLD}10`,border:`1px solid ${GOLD}40`,marginBottom:14}}>
              <div style={{width:90,aspectRatio:"16/9",background:"#000",borderRadius:6,overflow:"hidden",flexShrink:0}}>
                {selected.thumbnail_url && <img src={selected.thumbnail_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:700,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selected.title}</div>
                <div style={{fontSize:10.5,color:GOLD,letterSpacing:.4,textTransform:"uppercase",marginTop:2}}>{selected.kind}</div>
              </div>
            </div>

            {/* Existing notes — quick-attach by clicking */}
            {Array.isArray(notes) && notes.length > 0 && (
              <>
                <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.5)",letterSpacing:1.4,textTransform:"uppercase",marginBottom:6}}>Or pick an existing note</div>
                <div style={{maxHeight:160,overflow:"auto",display:"flex",flexDirection:"column",gap:5,marginBottom:14,paddingRight:4}}>
                  {notes.slice(0, 20).map(n => (
                    <button key={n.id} onClick={()=>attach(n)}
                      style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:8,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",color:"#F5F5F5",fontFamily:"inherit",cursor:"pointer",textAlign:"left"}}>
                      <span style={{fontSize:10,fontWeight:800,color:GOLD,fontFamily:"'JetBrains Mono', monospace",background:`${GOLD}14`,padding:"2px 6px",borderRadius:4,minWidth:46,textAlign:"center"}}>
                        {formatTime(n.timestamp_seconds)}
                      </span>
                      <span style={{flex:1,minWidth:0,fontSize:12.5,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {n.title || n.body || (n.image_url ? "Snapshot" : n.audio_url ? "Voice note" : "Untitled")}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Manual timestamp + title — for moments without a saved note */}
            <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.5)",letterSpacing:1.4,textTransform:"uppercase",marginBottom:6}}>Or pick a custom moment</div>
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
              <Clock size={13} color="rgba(255,255,255,0.5)"/>
              <input type="number" min={0} value={timestamp}
                onChange={e=>setTimestamp(Math.max(0, parseInt(e.target.value)||0))}
                placeholder="Seconds"
                style={{width:120,padding:"8px 10px",borderRadius:7,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.10)",color:"#fff",fontSize:13,fontFamily:"'JetBrains Mono', monospace",outline:"none"}}/>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>= {formatTime(timestamp)}</span>
            </div>
            <input value={titleOverride} onChange={e=>setTitleOverride(e.target.value)}
              placeholder="Caption / title for this moment"
              style={{width:"100%",padding:"9px 12px",borderRadius:8,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.10)",color:"#fff",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:14}}/>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8,paddingTop:8,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
              <button onClick={onClose}
                style={{padding:"9px 16px",borderRadius:8,background:"transparent",color:"rgba(255,255,255,0.6)",border:"1px solid rgba(255,255,255,0.15)",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                Cancel
              </button>
              <button onClick={()=>attach(null)}
                style={{padding:"9px 18px",borderRadius:8,background:`linear-gradient(180deg, ${GOLD} 0%, #d4ce8a 100%)`,color:"#111",border:"none",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",letterSpacing:.3,display:"inline-flex",alignItems:"center",gap:6,boxShadow:`0 6px 18px ${GOLD}33`}}>
                <Plus size={12}/> Attach
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FilmMomentPicker;
